import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, Typography, Row, Col, Card, message } from "antd";
import axiosInstance from "../../config/axios/axios.config";
import PropertyResults from "../../components/Toolbar/PropertyResults";
import { useNavigate, useParams } from "react-router-dom";
import FileListModal from "./FileListModal";
import ProgressBar from "./ProgressBar";
import ImageDragger from "./ImageDragger";
import { formattFiles, uploadAvvMediaFiles } from "../../helpers/imageProcessors";
import ImageCollectionCard from "./ImageCollectionCard";

const { Title } = Typography;

const ImageUpload = () => {
  const params = useParams();
  const reportNameRef = useRef(params?.reportName || null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchedResult, setSearchedResult] = useState(reportNameRef.current);
  const [fileCount, setFileCount] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const [imageCollections, setImageCollections] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);

  // Check AVV Access using memoized function
  const checkAvvAccess = useCallback(async () => {
    try {
      const response = await axiosInstance.post("/widgets/user/report_limit/", {
        address: reportNameRef.current,
      });
      if (!response?.data?.can_access) {
        message.error("You do not have access to this AVV report.");
        navigate("/");
      }
    } catch (error) {
      message.error("Error checking AVV report access. Please try again.");
      navigate("/error-route");
    }
  }, [navigate]);

  useEffect(() => {
    checkAvvAccess();
    return () => {
      isMounted.current = false;
    };
  }, [checkAvvAccess]);

  useEffect(() => {
    const fetchImageCollections = async () => {
      try {
        let url = "accounts/collections/user_collections/?avv=true";
        const response = await axiosInstance.get(url);
        setImageCollections(response.data);
      } catch (error) {
        console.error("Error fetching image collections:", error);
        message.error("Failed to load image collections");
      }
    };

    fetchImageCollections();
  }, []);

  const handleImageSelect = (image, isSelected) => {
    if (isSelected) {
      setSelectedImages((prev) => [...prev, image]);
    } else {
      setSelectedImages((prev) => prev.filter((img) => img.id !== image.id));
    }
    setFileCount((prev) => (isSelected ? prev + 1 : prev - 1));
  };

  const resetState = useCallback(() => {
    setSearchedResult(null);
    setFiles([]);
    setFileCount(0);
  }, []);

  const onFileChange = ({ fileList }) => {
    setFiles(fileList);
    setFileCount(fileList.length);
  };

  const onSave = async () => {
    if (
      !files &&
      files?.length === 0 &&
      !searchedResult &&
      loading &&
      !selectedImages &&
      selectedImages?.length === 0
    ) {
      message.error("Please select files and search for a property.");
      return;
    }
    setLoading(true);
    let imageUrls = [];
    try {
      if (files && files.length > 0) {
        const formattedFiles = await formattFiles(files);
        imageUrls = await uploadAvvMediaFiles(formattedFiles);
      }

      if (selectedImages && selectedImages.length > 0) {
        imageUrls = [
          ...imageUrls,
          ...selectedImages.map((image) => image.file_url),
        ];
      }
      const formData = new FormData();
      formData.append("address", searchedResult);
      formData.append("image_urls", JSON.stringify(imageUrls));
      await axiosInstance.post("widgets/avv/create/", formData);
      message.success("Images uploaded successfully.");
      resetState();
      navigate({ pathname: "/search" });
    } catch (error) {
      console.error("Error uploading:", error);
    } finally {
      if (isMounted.current) {
        setLoading(false);
        resetState();
      }
    }
  };

  const onCancel = () => {
    setFiles([]);
    setFileCount(0);
  };

  const showFileListModal = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  return (
    <Card
      title={<Title level={4}>Automated Vision Valuation</Title>}
      style={{
        marginTop: 70,
        width: "90%",
        marginLeft: "5%",
        marginRight: "5%",
      }}
    >
      <Row>
        <Col span={24} style={{ marginBottom: 20 }}>
          <PropertyResults
            setSearchedResult={setSearchedResult}
            placeholderText="Search for Property"
            showOnlyProperty={true}
            existingSearchText={reportNameRef.current}
          />
        </Col>
        <Col
          span={24}
          style={{
            marginBottom: 20,
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
          }}
        >
          {fileCount > 0 && (
            <Button onClick={showFileListModal}>{fileCount} Files</Button>
          )}
        </Col>
        <Col span={24}>
          <ImageDragger
            searchedResult={searchedResult}
            files={files}
            onFileChange={onFileChange}
          />
          <FileListModal
            files={files}
            isVisible={isModalVisible}
            onClose={handleModalClose}
          />
        </Col>
        {loading && <ProgressBar />}
        <Col
          span={24}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            alignItems: "center",
            height: "80px",
          }}
        >
          <Button onClick={onCancel} size="large" disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!searchedResult || loading || fileCount === 0}
            onClick={onSave}
            size="large"
          >
            RUN AVV
          </Button>
        </Col>

        <Col span={24} style={{ marginTop: 20 }}>
          <Title level={5}>Uploaded Images</Title>
          <Row gutter={[16, 16]}>
            {imageCollections.map((collection) => (
              <Col key={collection.id} xs={24} sm={12} md={8} lg={24}>
                <ImageCollectionCard
                  collection={collection}
                  onImageSelect={handleImageSelect}
                />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default ImageUpload;
